import type { HeadlineComment } from "@/local-shared/returnTypes";
import LikeIcon from "@/assets/img/symbolic/thumb-thin.svg?react";
import { useCallback, useContext, useEffect, useState } from "react";
import { formatNumber } from "@/util/Util";
import social from "@/api/social";
import { AuthContext } from "@/Realize";
import DEFAULT_AVATAR from "@/assets/img/r-icon.svg";
import type { TabName } from "@/pages/home/TabData";
import UIListener from "@/pages/home/UIListener";
import { S_IN_M } from "@/realize-shared/model/Time";

const HOURS_IN_DAY = 24;
const MAX_DAYS = 30;

export function TimeAgo({
    timestamp,
    shortForm = false,
    className,
}: {
    timestamp: Date;
    shortForm?: boolean;
    className?: string;
})
{
    const stringifyDate = useCallback((timestamp: Date) =>
    {
        const now = new Date();
        const diff = Math.abs(now.getTime() - new Date(timestamp).getTime());
        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / HOURS_IN_DAY);

        if (seconds < S_IN_M) return shortForm ? `${seconds}s` : `${seconds} seconds ago`;
        if (minutes < S_IN_M) return shortForm? `${minutes}m` : `${minutes} minutes ago`;
        if (hours < HOURS_IN_DAY) return shortForm ? `${hours}h` : `${hours} hours ago`;
        if (days === 1) return shortForm ? "1d" : "1 day ago";
        if (days < MAX_DAYS) return shortForm ? `${days}d` : `${days} days ago`;

        // Over 30 days old return the date formatted as "MMM DD, YYYY"
        const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "short",
            day: "numeric",
        };

        return new Date(timestamp).toLocaleDateString("en-US", options);
    }, [shortForm]);

    const [formattedDate, setFormattedDate] = useState(stringifyDate(timestamp));

    useEffect(() =>
    {
        const interval = setInterval(() =>
        {
            setFormattedDate(stringifyDate(timestamp));
        }, 1000);

        return () =>
        {
            clearInterval(interval);
        };
    }, [stringifyDate, timestamp]);

    return (
        <span className={className}>
            {formattedDate}
        </span>
    );
}

export default function DailyHeadlineComment({
    comment,
    inverted = false,
    setError,
}: {
    comment: HeadlineComment;
    inverted?: boolean;
    setError: React.Dispatch<React.SetStateAction<{ title: string; error: string } | null>>;
})
{
    const [liked, setLiked] = useState(comment.hasLiked ?? false);
    const [likes, setLikes] = useState<number|undefined>(comment.stats.likes);

    const userData = useContext(AuthContext);


    return (
        <div className="flex flex-row justify-between gap-2 p-4">
            <div className="flex items-start gap-2">
                <img
                    src={comment.avatarURL === "" || comment.avatarURL === null ? DEFAULT_AVATAR : comment.avatarURL}
                    alt={comment.username}
                    className={`w-10 h-10 
                    rounded-full bg-poppy-yellow ${comment.avatarURL === "" ? "object-contain p-1" : ""}`}
                />
                <div className={`flex flex-col ${inverted && "text-white"}`}>
                    <span
                        className="font-semibold"
                        onClick={(e) =>
                        {
                            e.stopPropagation();
                            localStorage.setItem("profile_id", comment.user_id.toString());

                            UIListener.trigger("setActiveTab", "profile" as TabName);
                        }}
                    >
                        {comment.username}
                    </span>

                    <p>
                        {comment.content}
                    </p>
                    <span className="text-sm text-gray-500">
                        <TimeAgo timestamp={comment.date} />
                    </span>
                </div>
            </div>
            <div>
                <div className="h-full flex flex-col items-end justify-end">
                    <button
                        onClick={() =>
                        {
                            if (userData !== null)
                            {
                                social.likeComment(comment.id).then((data) =>
                                {
                                    Logger.log("Like response:", data);

                                    if (data.success)
                                    {
                                        setLiked((prev) => !prev);

                                        setLikes((prev) =>
                                        {
                                            if (prev === undefined)
                                            {
                                                return liked ? 0 : 1;
                                            }

                                            return liked ? prev - 1 : prev + 1;
                                        });
                                    }
                                    else
                                    {
                                        const {message} = data;

                                        if (message)
                                        {
                                            setError({
                                                title: "Error liking comment",
                                                error: message,
                                            });
                                        }
                                        else
                                        {
                                            setError(null);
                                        }

                                        Logger.error("Error liking comment:", message);
                                    }
                                })
                                    .catch(() =>
                                    {
                                    // Useless error
                                    });
                            }
                        }}
                    >
                        <LikeIcon
                            className={`w-5 h-5 ${
                                liked ? inverted ? "text-poppy-yellow" : "text-sharp-red" : "text-[#888888]"
                            }`}
                        />
                        <span className="text-gray-500">
                            {typeof likes === "number" && likes > 0 && formatNumber(likes)}
                            {typeof likes === "number" && likes <= 0 && (<span>&nbsp;</span>)}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
}
